/**
 * v0.11.0 Coins — the canonical 金币 display. Per spec the design now
 * shows balances as INTEGERS (5,860) rather than decimal money strings.
 *
 * The backend still stores the smallest economy unit; Coins intentionally
 * treats that integer as app coins visually. So a kid with 5,860 units
 * sees the original yellow coin glyph + "5,860".
 *
 *   <Coins sen={5860} />
 *   <Coins sen={50} size="sm" />
 *   <Coins sen={120000} size="lg" />
 */
import type { CSSProperties } from 'react';

interface CoinsProps {
  sen: number;
  size?: 'sm' | 'md' | 'lg';
  /** When true, renders a +/- prefix (used in transaction lists). */
  signed?: boolean;
  style?: CSSProperties;
}

const SIZE_FONT: Record<NonNullable<CoinsProps['size']>, number> = {
  sm: 11,
  md: 13,
  lg: 18,
};

const ICON_SIZE: Record<NonNullable<CoinsProps['size']>, number> = {
  sm: 14,
  md: 16,
  lg: 24,
};

const COIN_GLYPH = '💰';

export function Coins({ sen, size = 'md', signed = false, style }: CoinsProps) {
  const v = Number(sen) || 0;
  const display = formatCoinsValue(Math.abs(v));
  const prefix = signed
    ? (v > 0 ? '+' : v < 0 ? '−' : '')
    : '';
  return (
    <span
      style={{
        fontSize: SIZE_FONT[size],
        fontWeight: 500,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        ...style,
      }}
    >
      <CoinIcon size={ICON_SIZE[size]} />
      {prefix}{display}
    </span>
  );
}

export function formatCoinsValue(sen: number): string {
  return Math.trunc(Number(sen) || 0).toLocaleString();
}

export function CoinIcon({ size = 16 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size,
        lineHeight: 1,
        verticalAlign: '-0.12em',
      }}
    >
      {COIN_GLYPH}
    </span>
  );
}
